import { useState, useEffect } from 'react'
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom'
import { UserRole, AdminType } from './types'
import { useAuth } from './hooks/useAuth'
import { hasFinancistAccess } from './lib/roleFinancialAccess'
import { isAttendanceOversightUser } from './lib/attendanceOversight'
import RequesterView from './pages/RequesterView'
import AdminView from './pages/AdminView'
import ExecutorView from './pages/ExecutorView'
import VedouciView from './pages/VedouciView'
import PurchaseLeaderView from './pages/PurchaseLeaderView'
import FinancistView from './pages/FinancistView'
import DirectorView from './pages/DirectorView'
import ReferentView from './pages/ReferentView'
import DirectorTicketsView from './pages/DirectorTicketsView'
import PropertyReferentView from './pages/PropertyReferentView'
import PropertyRequesterView from './pages/PropertyRequesterView'
import TravelOrdersView from './pages/TravelOrdersView'
import AuthCallback from './pages/AuthCallback'
import IntranetHome from './pages/IntranetHome'
import MyCabinet from './pages/MyCabinet'
import CalendarView from './pages/CalendarView'
import GuestView from './pages/GuestView'
import PublicCanteenVoting from './pages/PublicCanteenVoting'
import PublicCanteenStatistics from './pages/PublicCanteenStatistics'
import { FkspSelectRole } from './pages/FkspSelectRole'
import { FkspView } from './pages/FkspView'
import { FkspPersonalistaView } from './pages/FkspPersonalistaView'
import { FkspZocmosView } from './pages/FkspZocmosView'
import { FkspDirectorView } from './pages/FkspDirectorView'
import { FkspEkonomView } from './pages/FkspEkonomView'
import { FkspDeletedView } from './pages/FkspDeletedView'
import DovolenaView from './pages/DovolenaView'
import DovolenaSharePage from './pages/DovolenaSharePage'
import CanteenKiosk from './pages/CanteenKiosk'
import PublicEventsCalendar from './pages/PublicEventsCalendar'
import PrivacyPolicy from './pages/PrivacyPolicy'
import SchoolEmployees from './pages/SchoolEmployees'
import ITAdminInfo from './pages/ITAdminInfo'
import ITAdminRestrictedTable from './pages/ITAdminRestrictedTable'
import AttendanceView from './pages/AttendanceView'
import AttendanceSelectRole from './pages/AttendanceSelectRole'
import AttendanceUserView from './pages/AttendanceUserView'
import AttendanceLeaderView from './pages/AttendanceLeaderView'
import AttendancePersonalistaManageView from './pages/AttendancePersonalistaManageView'
import VehicleFleetSelectRole from './pages/VehicleFleetSelectRole'
import VehicleFleetAdminView from './pages/VehicleFleetAdminView'
import VehicleFleetManagerView from './pages/VehicleFleetManagerView'
import VehicleFleetDriverView from './pages/VehicleFleetDriverView'
import Auth from './components/Auth'
import RoleSelector from './components/RoleSelector'
import PurchaseRoleSelector from './components/PurchaseRoleSelector'
import PropertyRoleSelector from './components/PropertyRoleSelector'
import DovolenaRoleSelector from './components/DovolenaRoleSelector'
import { labelDovolenaUiRole, userCanSelectDovolenaRole, type DovolenaUiRole } from './lib/dovolenaRoles'
import { savePostLoginRedirect } from './lib/postLoginRedirect'
import { PWAUpdatePrompt } from './components/PWAUpdatePrompt'
import { ModuleDevRouteGuard } from './components/ModuleDevRouteGuard'
import CookieConsent from './components/CookieConsent'
import { Header } from './components/Header'
import { CabinetReturnBar } from './components/CabinetReturnBar'
import { AlertProvider } from './contexts/AlertContext'
import { CABINET_PATH, hasCabinetReturnFlag } from './lib/cabinetReturn'
import './App.css'
import './styles/common-buttons.css'

const PURCHASE_UI_ROLES = ['director', 'financist', 'referent', 'vedouci', 'zadatel'] as const
type PurchaseUiRole = (typeof PURCHASE_UI_ROLES)[number]

function isPurchaseUiRole(v: string | null): v is PurchaseUiRole {
  return !!v && (PURCHASE_UI_ROLES as readonly string[]).includes(v)
}

function App() {
  // ВСЕ хуки должны быть вызваны ДО любого условного рендеринга
  const { user, loading, authenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  
  const [currentRole, setCurrentRole] = useState<UserRole | null>(() => {
    try {
      const path = window.location.pathname
      const role = new URLSearchParams(window.location.search).get('role')
      if (
        (path === '/app' || path === '/app/select-role') &&
        (role === 'executor' || role === 'requester' || role === 'admin')
      ) {
        return role
      }
    } catch {
      /* */
    }
    const savedRole = localStorage.getItem('currentRole') as UserRole | null
    return savedRole || null
  })
  const [adminType, setAdminType] = useState<AdminType | null>(() => {
    try {
      const path = window.location.pathname
      const params = new URLSearchParams(window.location.search)
      const role = params.get('role')
      const at = params.get('adminType') as AdminType | null
      if ((path === '/app' || path === '/app/select-role') && role === 'admin') {
        if (at) return at
      }
    } catch {
      /* */
    }
    const savedAdminType = localStorage.getItem('adminType') as AdminType | null
    return savedAdminType || null
  })
  const [purchaseRole, setPurchaseRole] = useState<PurchaseUiRole | null>(() => {
    try {
      const path = window.location.pathname
      const role = new URLSearchParams(window.location.search).get('role')
      if (
        (path === '/purchase-requests' || path === '/purchase-requests/select-role') &&
        isPurchaseUiRole(role)
      ) {
        return role
      }
    } catch {
      /* */
    }
    const saved = localStorage.getItem('purchaseRole')
    return isPurchaseUiRole(saved) ? saved : null
  })
  const [propertyRole, setPropertyRole] = useState<'property_referent' | 'requester' | null>(() => {
    try {
      const path = window.location.pathname
      const role = new URLSearchParams(window.location.search).get('role')
      if (
        (path === '/property' || path === '/property/select-role') &&
        (role === 'requester' || role === 'property_referent')
      ) {
        return role
      }
    } catch {
      /* */
    }
    const saved = localStorage.getItem('propertyRole') as 'property_referent' | 'requester' | null
    return saved || null
  })
  const [dovolenaRole, setDovolenaRole] = useState<DovolenaUiRole | null>(() => {
    try {
      const path = window.location.pathname
      const role = new URLSearchParams(window.location.search).get('role')
      if (
        (path === '/dovolena' || path === '/dovolena/select-role') &&
        role &&
        ['zadatel', 'vedouci', 'personalista', 'spravce_rozvrhu', 'nastaveni'].includes(role)
      ) {
        return role as DovolenaUiRole
      }
    } catch {
      /* */
    }
    const saved = localStorage.getItem('dovolenaRole') as DovolenaUiRole | null
    return saved || null
  })

  // Сохраняем роли в localStorage при изменении
  useEffect(() => {
    if (currentRole) {
      localStorage.setItem('currentRole', currentRole)
    } else {
      localStorage.removeItem('currentRole')
    }
  }, [currentRole])

  useEffect(() => {
    if (adminType) {
      localStorage.setItem('adminType', adminType)
    } else {
      localStorage.removeItem('adminType')
    }
  }, [adminType])

  useEffect(() => {
    if (purchaseRole) {
      localStorage.setItem('purchaseRole', purchaseRole)
    } else {
      localStorage.removeItem('purchaseRole')
    }
  }, [purchaseRole])

  useEffect(() => {
    if (propertyRole) {
      localStorage.setItem('propertyRole', propertyRole)
    } else {
      localStorage.removeItem('propertyRole')
    }
    if (dovolenaRole) {
      localStorage.setItem('dovolenaRole', dovolenaRole)
    } else {
      localStorage.removeItem('dovolenaRole')
    }
  }, [propertyRole, dovolenaRole])

  // Очищаем роли при выходе
  useEffect(() => {
    if (!loading && !authenticated) {
      // Если не аутентифицирован, сбрасываем роль
      setCurrentRole(null)
      setAdminType(null)
      setPurchaseRole(null)
      setPropertyRole(null)
      setDovolenaRole(null)
      localStorage.removeItem('currentRole')
      localStorage.removeItem('adminType')
      localStorage.removeItem('purchaseRole')
      localStorage.removeItem('propertyRole')
      localStorage.removeItem('dovolenaRole')
    }
  }, [loading, authenticated])

  useEffect(() => {
    if (!user || !dovolenaRole) return
    if (!userCanSelectDovolenaRole(user, dovolenaRole)) {
      setDovolenaRole(null)
      localStorage.removeItem('dovolenaRole')
    }
  }, [user, dovolenaRole])

  // Deep-link z ranního digestu / e-mailu / Můj kabinet: ?role=… nastaví modulovou roli
  useEffect(() => {
    if (!authenticated || !user) return
    const params = new URLSearchParams(location.search)
    const roleParam = params.get('role')

    if (
      (location.pathname === '/purchase-requests' ||
        location.pathname === '/purchase-requests/select-role') &&
      isPurchaseUiRole(roleParam) &&
      purchaseRole !== roleParam
    ) {
      setPurchaseRole(roleParam)
      if (location.pathname === '/purchase-requests/select-role') {
        navigate('/purchase-requests' + location.search, { replace: true })
      }
    }

    if (
      (location.pathname === '/dovolena' || location.pathname === '/dovolena/select-role') &&
      roleParam &&
      userCanSelectDovolenaRole(user, roleParam as DovolenaUiRole) &&
      dovolenaRole !== roleParam
    ) {
      setDovolenaRole(roleParam as DovolenaUiRole)
      if (location.pathname === '/dovolena/select-role') {
        navigate('/dovolena' + location.search, { replace: true })
      }
    }

    // Majetek
    if (
      (location.pathname === '/property' || location.pathname === '/property/select-role') &&
      (roleParam === 'requester' || roleParam === 'property_referent') &&
      propertyRole !== roleParam
    ) {
      setPropertyRole(roleParam)
      if (location.pathname === '/property/select-role') {
        navigate('/property' + location.search, { replace: true })
      }
    }

    // Závady / app role (executor, admin, requester)
    if (location.pathname === '/app' || location.pathname === '/app/select-role') {
      if (roleParam === 'executor' && currentRole !== 'executor') {
        setCurrentRole('executor')
        if (location.pathname === '/app/select-role') {
          navigate('/app' + location.search, { replace: true })
        }
      } else if (roleParam === 'requester' && currentRole !== 'requester') {
        setCurrentRole('requester')
        if (location.pathname === '/app/select-role') {
          navigate('/app' + location.search, { replace: true })
        }
      } else if (roleParam === 'admin' && user.role === 'admin') {
        const at = (params.get('adminType') || user.admin_type || null) as AdminType | null
        if (currentRole !== 'admin' || adminType !== at) {
          setCurrentRole('admin')
          if (at) setAdminType(at)
        }
        if (location.pathname === '/app/select-role') {
          navigate('/app' + location.search, { replace: true })
        }
      }
    }
  }, [
    authenticated,
    user,
    location.pathname,
    location.search,
    purchaseRole,
    dovolenaRole,
    propertyRole,
    currentRole,
    adminType,
    navigate,
  ])

  // Определяем публичные пути ДО проверки загрузки
  const publicPaths = ['/auth/callback', '/guest', '/public-canteen', '/public-canteen/statistics', '/public-canteen/kiosk', '/calendar', '/calendar-events', '/privacy-policy', '/gdpr', '/school-employees']
  const isPublicPath = publicPaths.some(path => location.pathname.startsWith(path))

  // Před OAuth uložit cílovou URL (deep-link z e-mailu)
  useEffect(() => {
    if (loading || authenticated || isPublicPath) return
    const ret = location.pathname + location.search
    savePostLoginRedirect(ret)
  }, [loading, authenticated, isPublicPath, location.pathname, location.search])

  // Показываем загрузку только если не на странице callback и не на публичных страницах
  if (loading && location.pathname !== '/auth/callback' && !isPublicPath) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #14b8a6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <div>Načítání...</div>
        </div>
      </div>
    )
  }

  // Если не аутентифицирован и не на странице callback - показываем экран входа
  // НЕ показываем Auth на странице выбора типа пользователя, странице гостей, календаре, столовой и политике конфиденциальности - там своя логика
  
  // НЕ показываем Auth сразу - даем время для загрузки данных
  // Это предотвращает белый экран при обновлении страницы
  if (!loading && !authenticated && !isPublicPath && user === null) {
    savePostLoginRedirect(location.pathname + location.search)
    return <Auth onAuthSuccess={() => navigate('/intranet')} />
  }

  // Если аутентифицирован, но роль не выбрана - показываем экран выбора роли
  if (authenticated && user && !currentRole && location.pathname !== '/auth/callback') {
    // Показываем RoleSelector на главной странице
    // Это обрабатывается в Route "/" ниже
  }

  // Проверяем, находимся ли мы на странице киоска (где не нужны куки)
  const isKioskPage = location.pathname === '/public-canteen/kiosk'

  return (
    <AlertProvider>
      <PWAUpdatePrompt />
      {!isKioskPage && <CookieConsent />}
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CabinetReturnBar />
      <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/select-user-type" element={<Navigate to="/intranet" replace />} />
      <Route path="/guest" element={<GuestView />} />
      <Route path="/public-canteen" element={<PublicCanteenVoting />} />
      <Route path="/student" element={<Navigate to="/" replace />} />
      <Route path="/public-canteen/statistics" element={<PublicCanteenStatistics />} />
      <Route path="/public-canteen/kiosk" element={<CanteenKiosk />} />
      <Route path="/intranet" element={
        authenticated && user ? (
          <IntranetHome />
        ) : (
          <Auth onAuthSuccess={() => navigate('/intranet', { replace: true })} />
        )
      } />
      <Route path="/my-cabinet" element={
        authenticated && user ? (
          <MyCabinet />
        ) : (
          <Auth onAuthSuccess={() => navigate('/my-cabinet', { replace: true })} />
        )
      } />
      <Route path="/calendar" element={<CalendarView />} />
      <Route path="/calendar-events" element={<PublicEventsCalendar />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/gdpr" element={<PrivacyPolicy />} />
      <Route path="/school-employees" element={<SchoolEmployees />} />
      <Route path="/it-admin-info" element={
        authenticated && user ? (
          <ITAdminInfo />
        ) : (
          <Auth onAuthSuccess={() => navigate('/it-admin-info')} />
        )
      } />
      <Route path="/it-admin-info/prehled" element={
        authenticated && user ? (
          <ITAdminRestrictedTable />
        ) : (
          <Auth onAuthSuccess={() => navigate('/it-admin-info/prehled')} />
        )
      } />
      <Route path="/purchase-requests/select-role" element={
        authenticated && user ? (
          <PurchaseRoleSelector 
            onRoleSelect={(role) => {
              setPurchaseRole(role)
              const params = new URLSearchParams(location.search)
              params.delete('role')
              const rest = params.toString()
              navigate('/purchase-requests' + (rest ? `?${rest}` : ''))
            }}
            currentUser={user}
          />
        ) : (
          <Auth onAuthSuccess={() => navigate('/intranet')} />
        )
      } />
      <Route path="/property/select-role" element={
        authenticated && user ? (
          <PropertyRoleSelector 
            onRoleSelect={(role) => {
              setPropertyRole(role)
              const params = new URLSearchParams(location.search)
              params.delete('role')
              const rest = params.toString()
              navigate('/property' + (rest ? `?${rest}` : ''))
            }}
            currentUser={user}
          />
        ) : (
          <Auth onAuthSuccess={() => navigate('/intranet')} />
        )
      } />
      <Route path="/purchase-requests" element={
        authenticated && user && purchaseRole ? (
          <div className="app">
            <Header
              leftButtons={
                <button onClick={() => {
                  if (hasCabinetReturnFlag(location.search)) {
                    navigate(CABINET_PATH)
                    return
                  }
                  setPurchaseRole(null)
                  navigate('/purchase-requests/select-role')
                }} className="back-button">
                  {hasCabinetReturnFlag(location.search) ? '← Můj kabinet' : '← Zpět'}
                </button>
              }
              title={
                purchaseRole === 'zadatel' ? 'Žadatel' :
                purchaseRole === 'vedouci' ? 'Vedoucí' :
                purchaseRole === 'director' ? 'Ředitel' :
                purchaseRole === 'financist' ? 'Hlavní ekonom' :
                purchaseRole === 'referent' ? 'Obchodní referent' : ''
              }
              rightContent={
                user && (
                  <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>
                    {user.name}
                  </span>
                )
              }
            />
            {purchaseRole === 'zadatel' && <VedouciView purchaseRole={purchaseRole} />}
            {purchaseRole === 'vedouci' && <PurchaseLeaderView purchaseRole={purchaseRole} />}
            {purchaseRole === 'director' && <DirectorView purchaseRole={purchaseRole} />}
            {purchaseRole === 'financist' && <FinancistView purchaseRole={purchaseRole} />}
            {purchaseRole === 'referent' && <ReferentView purchaseRole={purchaseRole} />}
          </div>
        ) : authenticated && user ? (
          <Navigate to={`/purchase-requests/select-role${location.search}`} replace />
        ) : (
          <Auth onAuthSuccess={() => navigate('/intranet')} />
        )
      } />
      <Route path="/property" element={
        authenticated && user && propertyRole ? (
          <div className="app">
            <Header
              leftButtons={
                <button onClick={() => {
                  if (hasCabinetReturnFlag(location.search)) {
                    navigate(CABINET_PATH)
                    return
                  }
                  setPropertyRole(null)
                  navigate('/property/select-role')
                }} className="back-button">
                  {hasCabinetReturnFlag(location.search) ? '← Můj kabinet' : '← Zpět'}
                </button>
              }
              title={
                propertyRole === 'property_referent' ? 'Referent správy majetku' :
                propertyRole === 'requester' ? 'Žadatel' : ''
              }
              rightContent={
                user && (
                  <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>
                    {user.name}
                  </span>
                )
              }
            />
            {propertyRole === 'property_referent' && <PropertyReferentView />}
            {propertyRole === 'requester' && <PropertyRequesterView />}
          </div>
        ) : authenticated && user ? (
          <Navigate to={`/property/select-role${location.search}`} replace />
        ) : (
          <Auth onAuthSuccess={() => navigate('/intranet')} />
        )
      } />
      <Route path="/travel-orders" element={
        authenticated && user ? (
          <ModuleDevRouteGuard module="travel_orders">
            <div className="app">
              <Header
                leftButtons={
                  <button
                    onClick={() =>
                      navigate(hasCabinetReturnFlag(location.search) ? CABINET_PATH : '/intranet')
                    }
                    className="back-button"
                  >
                    {hasCabinetReturnFlag(location.search) ? '← Můj kabinet' : '← Domů'}
                  </button>
                }
                title="Cestovní příkaz"
                rightContent={
                  user && (
                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>
                      {user.name}
                    </span>
                  )
                }
              />
              <TravelOrdersView />
            </div>
          </ModuleDevRouteGuard>
        ) : (
          <Auth onAuthSuccess={() => navigate('/intranet')} />
        )
      } />

      {/* FKSP Routes */}
      <Route path="/fksp/select-role" element={
        authenticated && user ? (
          <ModuleDevRouteGuard module="fksp">
            <FkspSelectRole />
          </ModuleDevRouteGuard>
        ) : (
          <Auth onAuthSuccess={() => navigate('/intranet')} />
        )
      } />
      <Route path="/fksp" element={
        authenticated && user ? (
          <ModuleDevRouteGuard module="fksp">
            <div className="app">
              <Header
                leftButtons={
                  <button
                    onClick={() =>
                      navigate(hasCabinetReturnFlag(location.search) ? CABINET_PATH : '/fksp/select-role')
                    }
                    className="back-button"
                  >
                    {hasCabinetReturnFlag(location.search) ? '← Můj kabinet' : '← Zpět'}
                  </button>
                }
                title="Žadatel"
                rightContent={<span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>{user?.name}</span>}
              />
              <FkspView />
            </div>
          </ModuleDevRouteGuard>
        ) : (
          <Auth onAuthSuccess={() => navigate('/intranet')} />
        )
      } />
      <Route path="/fksp/personalista" element={
        authenticated && user && (user.role === 'personalista' || (user.role === 'admin' && user.admin_type === 'spravce_it')) ? (
          <ModuleDevRouteGuard module="fksp">
            <div className="app">
              <Header
                leftButtons={
                  <button
                    onClick={() =>
                      navigate(hasCabinetReturnFlag(location.search) ? CABINET_PATH : '/fksp/select-role')
                    }
                    className="back-button"
                  >
                    {hasCabinetReturnFlag(location.search) ? '← Můj kabinet' : '← Zpět'}
                  </button>
                }
                title="Personalista"
                rightContent={<span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>{user?.name}</span>}
              />
              <FkspPersonalistaView />
            </div>
          </ModuleDevRouteGuard>
        ) : (
          <Auth onAuthSuccess={() => navigate('/intranet')} />
        )
      } />
      <Route path="/fksp/zocmos" element={
        authenticated && user && (user.role === 'zocmos' || (user.role === 'admin' && user.admin_type === 'spravce_it')) ? (
          <ModuleDevRouteGuard module="fksp">
            <div className="app">
              <Header
                leftButtons={
                  <button
                    onClick={() =>
                      navigate(hasCabinetReturnFlag(location.search) ? CABINET_PATH : '/fksp/select-role')
                    }
                    className="back-button"
                  >
                    {hasCabinetReturnFlag(location.search) ? '← Můj kabinet' : '← Zpět'}
                  </button>
                }
                title="ZO ČMOS"
                rightContent={<span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>{user?.name}</span>}
              />
              <FkspZocmosView />
            </div>
          </ModuleDevRouteGuard>
        ) : (
          <Auth onAuthSuccess={() => navigate('/intranet')} />
        )
      } />
      <Route path="/fksp/director" element={
        authenticated && user && (user.role === 'director' || (user.role === 'admin' && user.admin_type === 'spravce_it')) ? (
          <ModuleDevRouteGuard module="fksp">
            <div className="app">
              <Header
                leftButtons={
                  <button
                    onClick={() =>
                      navigate(hasCabinetReturnFlag(location.search) ? CABINET_PATH : '/fksp/select-role')
                    }
                    className="back-button"
                  >
                    {hasCabinetReturnFlag(location.search) ? '← Můj kabinet' : '← Zpět'}
                  </button>
                }
                title="Ředitel"
                rightContent={<span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>{user?.name}</span>}
              />
              <FkspDirectorView />
            </div>
          </ModuleDevRouteGuard>
        ) : (
          <Auth onAuthSuccess={() => navigate('/intranet')} />
        )
      } />
      <Route path="/fksp/ekonom" element={
        authenticated && user && hasFinancistAccess(user) ? (
          <ModuleDevRouteGuard module="fksp">
            <div className="app">
              <Header
                leftButtons={
                  <button
                    onClick={() =>
                      navigate(hasCabinetReturnFlag(location.search) ? CABINET_PATH : '/fksp/select-role')
                    }
                    className="back-button"
                  >
                    {hasCabinetReturnFlag(location.search) ? '← Můj kabinet' : '← Zpět'}
                  </button>
                }
                title="Hlavní ekonom — FKSP"
                rightContent={<span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>{user?.name}</span>}
              />
              <FkspEkonomView />
            </div>
          </ModuleDevRouteGuard>
        ) : (
          <Auth onAuthSuccess={() => navigate('/intranet')} />
        )
      } />

      <Route path="/fksp/smazane" element={
        authenticated && user ? (
          <ModuleDevRouteGuard module="fksp">
            <div className="app">
              <Header
                leftButtons={
                  <button
                    onClick={() =>
                      navigate(hasCabinetReturnFlag(location.search) ? CABINET_PATH : '/fksp/select-role')
                    }
                    className="back-button"
                  >
                    {hasCabinetReturnFlag(location.search) ? '← Můj kabinet' : '← Zpět'}
                  </button>
                }
                title="FKSP — smazané žádosti"
                rightContent={<span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>{user?.name}</span>}
              />
              <FkspDeletedView />
            </div>
          </ModuleDevRouteGuard>
        ) : (
          <Auth onAuthSuccess={() => navigate('/intranet')} />
        )
      } />

      <Route path="/dovolena/select-role" element={
        authenticated && user ? (
          <ModuleDevRouteGuard module="dovolena">
            <DovolenaRoleSelector
              onRoleSelect={(role) => {
                setDovolenaRole(role)
                const params = new URLSearchParams(location.search)
                params.delete('role')
                const rest = params.toString()
                navigate('/dovolena' + (rest ? `?${rest}` : ''))
              }}
              currentUser={user}
            />
          </ModuleDevRouteGuard>
        ) : (
          <Auth onAuthSuccess={() => navigate('/intranet')} />
        )
      } />

      <Route path="/dovolena" element={
        authenticated && user && dovolenaRole ? (
          <ModuleDevRouteGuard module="dovolena">
            <div className="app">
              <Header
                leftButtons={
                  <button
                    type="button"
                    onClick={() => {
                      if (hasCabinetReturnFlag(location.search)) {
                        navigate(CABINET_PATH)
                        return
                      }
                      setDovolenaRole(null)
                      navigate('/dovolena/select-role')
                    }}
                    className="back-button"
                  >
                    {hasCabinetReturnFlag(location.search) ? '← Můj kabinet' : '← Zpět'}
                  </button>
                }
                title="Žádosti o nepřítomnost"
                rightContent={
                  user && (
                    <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>
                      {user.name}
                      {dovolenaRole ? ` · ${labelDovolenaUiRole(dovolenaRole)}` : ''}
                    </span>
                  )
                }
              />
              <DovolenaView user={user} uiRole={dovolenaRole} />
            </div>
          </ModuleDevRouteGuard>
        ) : authenticated && user ? (
          <Navigate to={`/dovolena/select-role${location.search}`} replace />
        ) : (
          <Auth onAuthSuccess={() => navigate('/intranet')} />
        )
      } />

      <Route path="/dovolena/share/:shareToken" element={<DovolenaSharePage />} />

      <Route
        path="/attendance"
        element={
          authenticated && user ? (
            <AttendanceSelectRole />
          ) : (
            <Auth onAuthSuccess={() => navigate('/intranet')} />
          )
        }
      />

      <Route
        path="/attendance/user"
        element={
          authenticated && user ? (
            <AttendanceUserView />
          ) : (
            <Auth onAuthSuccess={() => navigate('/intranet')} />
          )
        }
      />

      <Route
        path="/attendance/leader"
        element={
          authenticated && user ? (
            <AttendanceLeaderView />
          ) : (
            <Auth onAuthSuccess={() => navigate('/intranet')} />
          )
        }
      />

      <Route
        path="/attendance/personalista/:section"
        element={
          authenticated && user ? (
            <AttendancePersonalistaManageView />
          ) : (
            <Auth onAuthSuccess={() => navigate('/intranet')} />
          )
        }
      />

      <Route
        path="/attendance/personalista"
        element={
          authenticated && user ? (
            <AttendanceLeaderView mode="personalista" />
          ) : (
            <Auth onAuthSuccess={() => navigate('/intranet')} />
          )
        }
      />

      <Route
        path="/attendance/admin"
        element={
          authenticated && user && isAttendanceOversightUser(user) ? (
            <div className="app">
              <Header
                leftButtons={
                  <button onClick={() => navigate('/attendance')} className="back-button">
                    ← Zpět na výběr role
                  </button>
                }
                title="Docházkový systém — Admin"
                rightContent={
                  <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>{user?.name}</span>
                }
              />
              <AttendanceView />
            </div>
          ) : authenticated && user ? (
            <Navigate to="/attendance" replace />
          ) : (
            <Auth onAuthSuccess={() => navigate('/intranet')} />
          )
        }
      />

      <Route
        path="/vehicle-fleet"
        element={
          authenticated && user ? (
            <VehicleFleetSelectRole />
          ) : (
            <Auth onAuthSuccess={() => navigate('/intranet')} />
          )
        }
      />
      <Route
        path="/vehicle-fleet/admin"
        element={
          authenticated && user ? (
            <VehicleFleetAdminView />
          ) : (
            <Auth onAuthSuccess={() => navigate('/intranet')} />
          )
        }
      />
      <Route
        path="/vehicle-fleet/fleet-admin"
        element={
          authenticated && user ? (
            <VehicleFleetManagerView />
          ) : (
            <Auth onAuthSuccess={() => navigate('/intranet')} />
          )
        }
      />
      <Route
        path="/vehicle-fleet/driver"
        element={
          authenticated && user ? (
            <VehicleFleetDriverView />
          ) : (
            <Auth onAuthSuccess={() => navigate('/intranet')} />
          )
        }
      />

      <Route path="/app/select-role" element={
        authenticated && user ? (
          <RoleSelector 
            onRoleSelect={(role, adminType) => {
              setCurrentRole(role)
              if (role === 'director') {
                navigate('/app/director')
              } else if (role === 'admin' && adminType) {
                setAdminType(adminType)
                navigate('/app')
              } else {
                navigate('/app')
              }
            }}
            currentUser={user}
          />
        ) : (
          <Auth onAuthSuccess={() => navigate('/intranet')} />
        )
      } />
      <Route path="/app/director" element={
        authenticated && user && currentRole === 'director' ? (
          <div className="app">
            <Header
              leftButtons={
                <button onClick={() => {
                  setCurrentRole(null)
                  navigate('/app/select-role')
                }} className="back-button">
                  ← Zpět
                </button>
              }
              title="Ředitel"
              rightContent={
                user && (
                  <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>
                    {user.name}
                  </span>
                )
              }
            />
            <DirectorTicketsView />
          </div>
        ) : (
          <Auth onAuthSuccess={() => navigate('/app/select-role')} />
        )
      } />
      <Route path="/app" element={
        authenticated && user ? (
          <div className="app">
            <Header
              leftButtons={
                <button onClick={() => {
                  if (hasCabinetReturnFlag(location.search)) {
                    navigate(CABINET_PATH)
                    return
                  }
                  setCurrentRole(null)
                  setAdminType(null)
                  navigate('/app/select-role')
                }} className="back-button">
                  {hasCabinetReturnFlag(location.search) ? '← Můj kabinet' : '← Zpět'}
                </button>
              }
              title={
                currentRole === 'requester' ? 'Žadatel' :
                currentRole === 'admin' ? (
                  adminType === 'spravce_it' ? 'Administrátor IT' : 
                  adminType === 'spravce_budov' ? 'Administrátor Budov' : 
                  'Administrátor'
                ) :
                currentRole === 'executor' ? 'Řešitel' : ''
              }
              rightContent={
                user && (
                  <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>
                    {user.name}
                  </span>
                )
              }
            />
            
            {currentRole === 'requester' && <RequesterView />}
            {currentRole === 'admin' && adminType && <AdminView adminType={adminType} />}
            {currentRole === 'executor' && <ExecutorView />}
            {!currentRole && (
              <Navigate to={`/app/select-role${location.search}`} replace />
            )}
          </div>
        ) : (
          <Auth onAuthSuccess={() => navigate('/app/select-role')} />
        )
      } />
      <Route path="/" element={
        authenticated && user ? (
          <Navigate to="/intranet" replace />
        ) : (
          <Auth onAuthSuccess={() => navigate('/intranet')} />
        )
      } />
    </Routes>
    </div>
    </AlertProvider>
  )
}

export default App



